"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import { apiRequest, setAuthToken, getAuthToken } from "../utils/api";

export interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: "USER" | "ADMIN" | "SUPER_ADMIN";
  profileImage?: string;
  phoneNumber?: string;
  deliveryAddress?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthContextType {
  user: UserProfile | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<any>;
  register: (payload: any) => Promise<any>;
  verifyOtp: (email: string, otp: string) => Promise<any>;
  logout: () => Promise<void>;
  updateProfile: (payload: { name?: string; phoneNumber?: string; profileImage?: string; deliveryAddress?: string }) => Promise<any>;
  changePassword: (payload: any) => Promise<any>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function parseJwt(token: string) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = async (currentToken: string) => {
    try {
      const decoded = parseJwt(currentToken);
      const isExpired = decoded?.exp && decoded.exp * 1000 <= Date.now();
      if (!decoded || isExpired) {
        setAuthToken(null);
        setToken(null);
        setUser(null);
        return;
      }
      
      const response = await apiRequest("/auth/profile", {
        headers: { Authorization: currentToken },
      });

      if (response?.success && response.data) {
        setUser({
          ...response.data,
          role: decoded.role || "USER", // Add the role decoded from JWT
        });
      }
    } catch {
      setAuthToken(null);
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const storedToken = getAuthToken();
    if (storedToken) {
      setToken(storedToken);
      fetchProfile(storedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await apiRequest("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });

      if (response?.success && response.data?.token) {
        const userToken = response.data.token;
        setToken(userToken);
        setAuthToken(userToken);
        await fetchProfile(userToken);
      }
      return response;
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  const register = async (payload: any) => {
    // Payload should be { name, email, password, phoneNumber, profileImage }
    return await apiRequest("/users/register", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  };

  const verifyOtp = async (email: string, otp: string) => {
    setIsLoading(true);
    try {
      const response = await apiRequest("/users/verify-otp", {
        method: "POST",
        body: JSON.stringify({ email, otp }),
      });

      if (response?.success && response.data?.token) {
        const userToken = response.data.token;
        setToken(userToken);
        setAuthToken(userToken);
        await fetchProfile(userToken);
      }
      return response;
    } catch (err) {
      setIsLoading(false);
      throw err;
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      await apiRequest("/auth/logout", { method: "POST" });
    } catch {
      // Local logout should still complete if the saved token is already invalid.
    } finally {
      setAuthToken(null);
      setToken(null);
      setUser(null);
      setIsLoading(false);
    }
  };

  const updateProfile = async (payload: { name?: string; phoneNumber?: string; profileImage?: string; deliveryAddress?: string }) => {
    const response = await apiRequest("/users/profile", {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    if (response?.success && response.data) {
      setUser((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          ...response.data,
        };
      });
    }
    return response;
  };

  const changePassword = async (payload: { oldPassword: string; newPassword: string }) => {
    return await apiRequest("/auth/change-password", {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  };

  const refreshProfile = async () => {
    if (token) {
      await fetchProfile(token);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        verifyOtp,
        logout,
        updateProfile,
        changePassword,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
